import { Link } from 'react-router-dom';
import CertificationLayout from '../../layouts/CertificationLayout';
import type { TocItem } from '../../layouts/GuideLayout';
import type { ExamDetails } from '../../layouts/CertificationLayout';
import { AffiliateCTA } from '../../components/content/AffiliateCTA';
import { ResourceLink } from '../../components/content/ResourceLink';
import { CalculatorEmbed } from '../../components/content/CalculatorEmbed';

const examDetails: ExamDetails = {
  examCode: 'AZ-104',
  examName: 'Microsoft Azure Administrator',
  passingScore: 700,
  examCostGBP: 165,
  skillsMeasured: [
    'Manage Azure identities and governance (15–20%)',
    'Implement and manage storage (20–25%)',
    'Deploy and manage Azure compute (20–25%)',
    'Implement and manage virtual networking (15–20%)',
    'Monitor and maintain Azure resources (10–15%)',
  ],
};

const tocItems: TocItem[] = [
  { id: 'what-is-az104', label: 'What is AZ-104?', level: 2 },
  { id: 'exam-topics', label: 'Exam Topic Breakdown', level: 2 },
  { id: 'uk-context', label: 'UK Context', level: 2 },
  { id: 'free-resources', label: 'Free Study Resources', level: 2 },
  { id: 'paid-resources', label: 'Paid Study Resources', level: 2 },
  { id: 'study-plan', label: '8-Week Study Plan', level: 2 },
  { id: 'calculator-resources', label: 'Calculator Resources', level: 2 },
  { id: 'official-resources', label: 'Official Resources', level: 2 },
];

const topCta = (
  <AffiliateCTA
    programme="Pluralsight"
    courseName="AZ-104 Microsoft Azure Administrator"
    url="https://www.pluralsight.com/paths/az-104-microsoft-azure-administrator-associate"
    price="From £29/month"
    badgeText="Most Comprehensive"
  />
);

const bottomCta = (
  <AffiliateCTA
    programme="MeasureUp"
    courseName="AZ-104 Official Practice Test"
    url="https://www.measureup.com/microsoft-practice-test-az-104-microsoft-azure-administrator.html"
    price="From £99"
    badgeText="Official Microsoft Partner"
  />
);

export default function AZ104StudyGuidePage() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'AZ-104 Study Guide UK 2026',
            description: 'Complete AZ-104 Azure Administrator study guide for UK engineers.',
            author: { '@type': 'Organization', name: 'AzureCalc.uk' },
            publisher: {
              '@type': 'Organization',
              name: 'AzureCalc.uk',
              url: 'https://www.azure-calc.co.uk',
            },
            dateModified: '2026-03-30',
            url: 'https://www.azure-calc.co.uk/certifications/az-104-study-guide-uk',
          }),
        }}
      />
      <CertificationLayout
        title="AZ-104 Study Guide UK 2026"
        description="Complete AZ-104 study guide for UK engineers 2026. Azure Administrator exam domains, study plan, free and paid resources. The core ops certification for Azure professionals."
        lastUpdated="March 2026"
        tocItems={tocItems}
        examDetails={examDetails}
        relatedCalculators={[
          { service: 'blob-storage', path: '/blob-storage', label: 'Blob Storage Calculator' },
          { service: 'app-service', path: '/app-service', label: 'App Service Calculator' },
        ]}
        topCta={topCta}
        bottomCta={bottomCta}
        canonical="https://www.azure-calc.co.uk/certifications/az-104-study-guide-uk"
        metaTitle="AZ-104 Study Guide UK 2026 | AzureCalc.uk"
        metaDescription="Complete AZ-104 Azure Administrator study guide for UK engineers. Exam domains, 8-week study plan, free and paid resources."
      >
        {/* SECTION 1 */}
        <section id="what-is-az104">
          <h2>What is AZ-104?</h2>
          <p>
            AZ-104 is the core Azure operations certification — Microsoft Azure Administrator
            Associate. It validates the skills required to manage Azure subscriptions, implement
            storage solutions, deploy compute resources, configure virtual networking, and monitor
            Azure environments. It is the natural next step after AZ-900 for anyone in an
            operations, infrastructure, or cloud engineering role.
          </p>
          <p>
            The target audience is Azure administrators responsible for managing cloud
            infrastructure — people who create and manage VMs, configure networking, handle
            storage accounts, and ensure the environment is running reliably. AZ-104 is
            recommended to hold AZ-900 as a prerequisite, though it is not formally required.
          </p>
          <p>
            The exam consists of 40–60 questions including performance-based lab tasks in a live
            Azure environment. Passing score is 700/1000 and the cost is £165 via Pearson VUE.
            No retirement date has been announced.
          </p>
        </section>

        {/* SECTION 2 */}
        <section id="exam-topics">
          <h2>Exam Topic Breakdown</h2>
          <div className="not-prose flex flex-col gap-4 my-6">
            {[
              {
                domain: 'Domain 1',
                title: 'Manage Azure Identities and Governance',
                pct: '15–20%',
                items: [
                  'Microsoft Entra ID users, groups, and licences',
                  'RBAC roles and custom role definitions',
                  'Azure Policy and management groups',
                  'Subscriptions and cost management',
                ],
              },
              {
                domain: 'Domain 2',
                title: 'Implement and Manage Storage',
                pct: '20–25%',
                items: [
                  'Storage accounts and access keys',
                  'Blob Storage tiers and lifecycle management policies',
                  'Azure Files and Azure File Sync',
                  'Storage security: SAS tokens, stored access policies',
                ],
              },
              {
                domain: 'Domain 3',
                title: 'Deploy and Manage Azure Compute Resources',
                pct: '20–25%',
                items: [
                  'Virtual machines and VM scale sets',
                  'Azure App Service and Azure Container Instances',
                  'Azure Kubernetes Service basics',
                  'ARM templates and Bicep deployments',
                ],
              },
              {
                domain: 'Domain 4',
                title: 'Implement and Manage Virtual Networking',
                pct: '15–20%',
                items: [
                  'Virtual networks, subnets, and VNet peering',
                  'Network Security Groups and Application Security Groups',
                  'Azure DNS and Azure Load Balancer',
                  'VPN Gateway and ExpressRoute basics',
                ],
              },
              {
                domain: 'Domain 5',
                title: 'Monitor and Maintain Azure Resources',
                pct: '10–15%',
                items: [
                  'Azure Monitor and Log Analytics workspaces',
                  'Alerts and action groups',
                  'Azure Backup and Azure Site Recovery',
                  'Update Management',
                ],
              },
            ].map((d) => (
              <div key={d.domain} className="rounded p-5" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>
                      {d.domain}
                    </span>
                    <p className="text-[14px] font-bold mt-0.5" style={{ color: 'var(--text-primary)' }}>{d.title}</p>
                  </div>
                  <span className="text-[12px] font-semibold text-[#0078d4] border border-[#0078d4] px-2 py-0.5 rounded shrink-0" style={{ backgroundColor: 'var(--surface-container-low)' }}>
                    {d.pct}
                  </span>
                </div>
                <ul className="flex flex-col gap-1.5">
                  {d.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                      <span className="text-[#0078d4] shrink-0 mt-0.5">›</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <div className="not-prose mb-8">
          <p className="text-[14px] mb-3" style={{ color: 'var(--text-tertiary)' }}>
            Domain 2 covers Azure Blob Storage tiers and lifecycle policies in depth. Use our free calculator to understand the pricing:
          </p>
          <CalculatorEmbed service="blob-storage" />
        </div>

        {/* SECTION 3 */}
        <section id="uk-context">
          <h2>UK Context</h2>
          <p>
            AZ-104 is the most in-demand Azure operations certification in UK cloud job postings
            after AZ-900. UK salary ranges for Azure Administrator roles are typically £45,000–
            £70,000, with senior roles in London and financial services regularly exceeding this.
          </p>
          <p>
            Many UK managed service providers (MSPs) and IT departments require AZ-104 for staff
            managing Azure environments. It is also a common contractor requirement across UK
            public sector and financial services Azure programmes. Like AZ-500, AZ-104 includes
            hands-on lab tasks — you cannot prepare with MCQ practice tests alone.
          </p>
        </section>

        <div className="not-prose mb-8">
          <p className="text-[14px] mb-3" style={{ color: 'var(--text-tertiary)' }}>
            Domain 5 covers Azure Monitor and Log Analytics — understand the costs before your exam:
          </p>
          <CalculatorEmbed service="log-analytics" />
        </div>

        {/* SECTION 4 */}
        <section id="free-resources">
          <h2>Free Study Resources</h2>
          <p>
            AZ-104 requires more depth than AZ-900 — the free Microsoft Learn path alone is
            unlikely to be sufficient for most candidates. However, it is an essential foundation
            alongside the official practice assessment.
          </p>
          <div className="not-prose flex flex-col gap-2 my-4">
            {[
              {
                label: 'Microsoft Learn — Official AZ-104 learning path',
                detail: 'Approximately 40 hours of structured content across all 5 domains',
              },
              {
                label: 'AZ-104 official practice assessment',
                detail: 'Free on Microsoft Learn — identifies weak domains before exam',
              },
              {
                label: "John Savill's AZ-104 YouTube series",
                detail: 'Comprehensive free video course, highly rated by UK candidates',
              },
            ].map((r) => (
              <div
                key={r.label}
                className="flex items-start gap-3 px-4 py-3 rounded" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
              >
                <span className="material-symbols-outlined text-[#0078d4] text-[18px] mt-0.5 shrink-0">
                  check_circle
                </span>
                <div>
                  <p className="text-[13px] font-semibold" style={{ color: 'var(--text-secondary)' }}>{r.label}</p>
                  <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>{r.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* SECTION 5 */}
        <section id="paid-resources">
          <h2>Paid Study Resources</h2>
          <p>
            AZ-104 is significantly harder than AZ-900. Paid video courses and practice tests
            are recommended for most candidates — especially the lab component, which requires
            hands-on practice you cannot get from free text-based resources alone.
          </p>
          <div className="not-prose flex flex-col gap-4 my-6">
            <AffiliateCTA
              programme="Pluralsight"
              courseName="AZ-104 Microsoft Azure Administrator"
              url="https://www.pluralsight.com/paths/az-104-microsoft-azure-administrator-associate"
              price="From £29/month"
              badgeText="Most Comprehensive"
            />
            <AffiliateCTA
              programme="MeasureUp"
              courseName="AZ-104 Official Practice Test"
              url="https://www.measureup.com/microsoft-practice-test-az-104-microsoft-azure-administrator.html"
              price="From £99"
              badgeText="Official Microsoft Partner"
            />
            <AffiliateCTA
              programme="Whizlabs"
              courseName="AZ-104 Practice Tests"
              url="https://www.whizlabs.com/microsoft-azure-administrator-az-104/"
              price="From £15"
              badgeText="Budget Option"
            />
          </div>
        </section>

        {/* SECTION 6 */}
        <section id="study-plan">
          <h2>8-Week Study Plan</h2>
          <p>
            AZ-104 requires more preparation than AZ-900. Allow 8 weeks part-time (1–2 hours/day)
            or 4 weeks full-time. The lab component means hands-on practice in a real or trial
            Azure subscription is mandatory — not optional.
          </p>
          <div className="not-prose flex flex-col gap-3 my-6">
            {[
              {
                week: 'Week 1',
                title: 'Identity and Governance (Domain 1)',
                detail: 'Entra ID users and groups, RBAC custom roles, Azure Policy, management groups',
              },
              {
                week: 'Week 2',
                title: 'Storage (Domain 2)',
                detail: 'Storage accounts, Blob tiers and lifecycle, Azure Files, SAS tokens',
              },
              {
                week: 'Weeks 3–4',
                title: 'Compute (Domain 3)',
                detail: 'VMs and scale sets, App Service, Container Instances, ARM/Bicep',
              },
              {
                week: 'Week 5',
                title: 'Networking (Domain 4)',
                detail: 'VNets, NSGs, DNS, Load Balancer, VPN Gateway basics',
              },
              {
                week: 'Week 6',
                title: 'Monitoring and Backup (Domain 5)',
                detail: 'Azure Monitor, Log Analytics, Backup, Site Recovery, Update Management',
              },
              {
                week: 'Week 7',
                title: 'Hands-on Labs Practice',
                detail: 'Complete all Microsoft Learn sandbox exercises; practice lab tasks under time pressure',
              },
              {
                week: 'Week 8',
                title: 'Practice Exams and Weak Areas',
                detail: 'MeasureUp full practice test, review flagged topics, book exam',
              },
            ].map((w) => (
              <div key={w.week} className="flex gap-4 rounded p-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
                <span className="text-[11px] font-bold text-[#0078d4] w-16 shrink-0 pt-0.5 uppercase tracking-wide">
                  {w.week}
                </span>
                <div>
                  <p className="text-[13px] font-semibold" style={{ color: 'var(--text-secondary)' }}>{w.title}</p>
                  <p className="text-[12px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{w.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* SECTION 7 */}
        <section id="calculator-resources">
          <h2>Calculator Resources</h2>
          <p>
            Domain 2 (Storage) maps directly to the Blob Storage calculator — understanding tier
            pricing is tested in the exam. Domain 3 (Compute) includes App Service plan selection,
            which our App Service calculator covers for UK South pricing.
          </p>
          <div className="not-prose flex flex-col gap-3 my-6">
            <CalculatorEmbed service="blob-storage" />
            <CalculatorEmbed service="app-service" />
          </div>
        </section>

        {/* SECTION 8 */}
        <section id="official-resources">
          <h2>Official Resources</h2>
          <div className="not-prose flex flex-col gap-2 my-4">
            <ResourceLink
              title="AZ-104 official certification page"
              url="https://learn.microsoft.com/en-us/credentials/certifications/azure-administrator/"
              source="Microsoft Learn"
            />
            <ResourceLink
              title="AZ-104 study guide (official)"
              url="https://learn.microsoft.com/en-us/credentials/certifications/resources/study-guides/az-104"
              source="Microsoft Learn"
            />
          </div>
        </section>
        {/* Related certifications */}
        <div className="not-prose mt-8">
          <h3 className="text-[13px] font-bold uppercase tracking-wide mb-4" style={{ color: 'var(--text-secondary)' }}>Related Certifications</h3>
          <div className="flex flex-col gap-3">
            {[
              {
                path: '/certifications/az-900-study-guide-uk',
                label: 'AZ-900 — Azure Fundamentals',
                detail: 'Start here if you haven\'t taken AZ-900',
                icon: 'school',
              },
              {
                path: '/certifications/sc-200-study-guide-uk',
                label: 'SC-200 — Microsoft Security Operations Analyst',
                detail: 'The security operations companion to AZ-104',
                icon: 'shield',
              },
              {
                path: '/certifications/az-500-study-guide-uk',
                label: 'AZ-500 — Microsoft Azure Security Engineer',
                detail: 'The security engineering path from AZ-104',
                icon: 'lock',
              },
            ].map((cert) => (
              <Link
                key={cert.path}
                to={cert.path}
                className="flex items-start gap-3 px-4 py-3 rounded transition-colors" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
              >
                <span className="material-symbols-outlined text-[18px] text-[#0078d4] shrink-0 mt-0.5">{cert.icon}</span>
                <div>
                  <p className="text-[13px] font-semibold" style={{ color: 'var(--text-secondary)' }}>{cert.label}</p>
                  <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>{cert.detail}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </CertificationLayout>
    </>
  );
}
